/**
 * Rating Modal Component
 * Allows users to rate a shop on 3 dimensions: Algehele indruk, Sfeer, Prijs
 * Supports photo upload (max 4 photos)
 * Only for authenticated users
 */

import { useState, useEffect } from "react";
import { X, Star, Trash2, Loader2, Camera, Heart, Banknote, CreditCard, Check, Car } from "lucide-react";
import { ImageCaptureInput } from "@/components/ImageCaptureInput";
import { CategoryIcon, categoryIconMap } from "@/components/CategoryIcon";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/lib/utils";
import { useAuth, getToken } from "@/lib/authContext";
import { useI18n } from "@/lib/i18n";
import { getCurrencyForCountry, detectUserCountry } from "@/lib/countryDetection";
import { apiRequest } from "@/lib/queryClient";

interface ImagePreview {
  file: File;
  preview: string;
  uploadedUrl?: string; // URL after upload
}

interface RatingModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  shopId: string;
  shopName: string;
  existingReview?: {
    rating_atmosphere: number;
    rating_service: number;
    rating_price: number;
    price_level?: number | null;
    parking_rating?: number | null;
    comment?: string | null;
    photoUrls?: string[];
    meta_has_charity?: boolean;
    meta_has_cash_payment?: boolean;
    meta_has_card_payment?: boolean;
    foundCategories?: string[]; // Assuming string[] based on CategoryIcon map keys
  } | null;
  onSuccess?: () => void;
}

/**
 * Render star rating input
 */
function StarRating({
  value,
  onChange,
  label,
}: {
  value: number;
  onChange: (value: number) => void;
  label: string;
}) {
  return (
    <div className="space-y-2">
      <Label className="text-sm font-medium">{label}</Label>
      <div className="flex items-center gap-1">
        {[1, 2, 3, 4, 5].map((star) => (
          <button
            key={star}
            type="button"
            onClick={() => onChange(star)}
            className="focus:outline-none transition-transform hover:scale-110"
          >
            <Star
              className={`h-8 w-8 ${star <= value
                ? "text-yellow-500 fill-yellow-500"
                : "text-muted-foreground fill-muted-foreground/20"
                }`}
            />
          </button>
        ))}
        <span className="ml-2 text-sm text-muted-foreground">
          {value > 0 ? `${value}/5` : ""}
        </span>
      </div>
    </div>
  );
}

/**
 * Render price level rating input (1-3 symbols)
 */
function PriceLevelRating({
  value,
  onChange,
  label,
  currency = "🪙",
}: {
  value: number;
  onChange: (value: number) => void;
  label: string;
  currency?: string;
}) {
  const { t } = useI18n();
  return (
    <div className="space-y-2">
      <Label className="text-sm font-medium">{label}</Label>
      <div className="flex items-center gap-2">
        {[1, 2, 3, 4, 5].map((level) => (
          <button
            key={level}
            type="button"
            onClick={() => onChange(level)}
            className={cn(
              "h-12 w-12 rounded-full border-2 flex items-center justify-center text-lg transition-all",
              level <= value
                ? "bg-pink-500 border-pink-500 text-white shadow-md"
                : "bg-white border-pink-200 text-pink-300 hover:border-pink-400 hover:text-pink-400"
            )}
          >
            {currency}
          </button>
        ))}
        {value > 0 && (
          <span className="ml-2 text-sm text-muted-foreground italic">
            {value <= 2 ? t("rating.priceLevel.cheap") : value === 3 ? t("rating.priceLevel.average") : value === 4 ? t("rating.priceLevel.expensive") : t("rating.priceLevel.exclusive")}
          </span>
        )}
      </div>
    </div>
  );
}

/**
 * Render parking setup (1-3 cars)
 */
function ParkingLevelRating({
  value,
  onChange,
  label,
}: {
  value: number;
  onChange: (value: number) => void;
  label: string;
}) {
  const { t } = useI18n();
  return (
    <div className="space-y-2">
      <Label className="text-sm font-medium">{label}</Label>
      <div className="flex items-center gap-2">
        {[1, 2, 3].map((level) => (
          <button
            key={level}
            type="button"
            onClick={() => onChange(level)}
            className={cn(
              "h-12 w-12 rounded-full border-2 flex items-center justify-center text-lg transition-all",
              level <= value
                ? "bg-primary border-primary text-white shadow-md"
                : "bg-white border-primary/20 text-primary/40 hover:border-primary/50 hover:text-primary/60"
            )}
          >
            <Car className="h-6 w-6" />
          </button>
        ))}
        {value > 0 && (
          <span className="ml-2 text-sm text-muted-foreground italic">
            {value === 1 ? t("rating.parkingLevel.bad") : value === 2 ? t("rating.parkingLevel.ok") : t("rating.parkingLevel.easy")}
          </span>
        )}
      </div>
    </div>
  );
}

export function RatingModal({
  open,
  onOpenChange,
  shopId,
  shopName,
  existingReview,
  onSuccess,
}: RatingModalProps) {
  const { t } = useI18n();
  const { user } = useAuth();
  const [ratingOverall, setRatingOverall] = useState(
    existingReview?.rating_service ?? 0
  );
  const [ratingAtmosphere, setRatingAtmosphere] = useState(
    existingReview?.rating_atmosphere ?? 0
  );
  // rating_price in DB is used for the star rating of Price/Value
  const [ratingPriceStars, setRatingPriceStars] = useState(
    existingReview?.rating_price ?? 0
  );
  const [priceLevel, setPriceLevel] = useState(
    existingReview?.price_level ?? 0
  );
  const [parkingRating, setParkingRating] = useState(
    existingReview?.parking_rating ?? 0
  );
  const [comment, setComment] = useState(existingReview?.comment ?? "");
  const [images, setImages] = useState<string[]>([]); // Array of base64 strings

  // New meta fields
  const [isCharity, setIsCharity] = useState(existingReview?.meta_has_charity ?? false);
  const [hasCash, setHasCash] = useState(existingReview?.meta_has_cash_payment ?? false);
  const [hasCard, setHasCard] = useState(existingReview?.meta_has_card_payment ?? false);
  const [selectedCategories, setSelectedCategories] = useState<string[]>(existingReview?.foundCategories ?? []);

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [pendingImages, setPendingImages] = useState<string[]>([]);

  // Load existing review data into state
  useEffect(() => {
    if (open && existingReview) {
      setRatingOverall(existingReview.rating_service ?? 0);
      setRatingAtmosphere(existingReview.rating_atmosphere ?? 0);
      setRatingPriceStars(existingReview.rating_price ?? 0);
      setPriceLevel(existingReview.price_level ?? 0);
      setParkingRating(existingReview.parking_rating ?? 0);
      setComment(existingReview.comment ?? "");
      setIsCharity(existingReview.meta_has_charity ?? false);
      setHasCash(existingReview.meta_has_cash_payment ?? false);
      setHasCard(existingReview.meta_has_card_payment ?? false);
      setSelectedCategories(existingReview.foundCategories ?? []);

      if (existingReview.photo_urls && Array.isArray(existingReview.photo_urls) && existingReview.photo_urls.length > 0) {
        setImages(existingReview.photo_urls);
      } else if (existingReview.photoUrls && Array.isArray(existingReview.photoUrls) && existingReview.photoUrls.length > 0) {
        setImages(existingReview.photoUrls);
      } else {
        setImages([]);
      }
    } else if (open && !existingReview) {
      // Reset for new review
      setImages([]);
      setRatingOverall(0);
      setRatingAtmosphere(0);
      setRatingPriceStars(0);
      setPriceLevel(0);
      setParkingRating(0);
      setComment("");
      setIsCharity(false);
      setHasCash(false);
      setHasCard(false);
      setSelectedCategories([]);
    }
  }, [open, existingReview]);

  // Check authentication
  useEffect(() => {
    if (open && !user) {
      setError(t("rating.loginRequired"));
    } else {
      setError(null);
    }
  }, [open, user]);


  async function handleSubmit() {
    if (!user) {
      setError(t("rating.loginRequired"));
      return;
    }

    // Validate ratings
    if (ratingOverall === 0 || ratingAtmosphere === 0 || ratingPriceStars === 0) {
      setError(t("rating.allRequired"));
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      await apiRequest("POST", `/api/shops/${shopId}/reviews`, {
        rating_atmosphere: ratingAtmosphere,
        rating_service: ratingOverall,
        rating_price: ratingPriceStars,
        price_level: priceLevel || null,
        parking_rating: parkingRating || null,
        comment: comment.trim() || null,
        photoUrls: images.slice(0, 4),
        meta_has_charity: isCharity,
        meta_has_cash_payment: hasCash,
        meta_has_card_payment: hasCard,
        foundCategories: selectedCategories,
      });

      // Success
      onSuccess?.();
      onOpenChange(false);
    } catch (err: any) {
      console.error("Error submitting review:", err);
      setError(err.message || t("rating.genericError"));
    } finally {
      setIsSubmitting(false);
    }
  }

  async function handleDelete() {
    if (!window.confirm(t("rating.confirmDelete"))) {
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      await apiRequest("DELETE", `/api/shops/${shopId}/reviews`);

      // Success
      onSuccess?.();
      onOpenChange(false);
    } catch (err: any) {
      console.error("Error deleting review:", err);
      setError(err.message || t("rating.genericError"));
    } finally {
      setIsSubmitting(false);
    }
  }

  // Calculate average rating -> Excludes Price Level now!
  const averageRating =
    ratingOverall > 0 && ratingAtmosphere > 0 && ratingPriceStars > 0
      ? ((ratingOverall + ratingAtmosphere + ratingPriceStars) / 3).toFixed(1)
      : null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{existingReview ? t("rating.editTitle") : t("rating.title")} {typeof shopName === 'string' ? shopName : (shopName as any)?.value || t("shops.title")}</DialogTitle>
          <DialogDescription>
            {t("rating.desc")}
          </DialogDescription>
        </DialogHeader>

        {!user && (
          <div className="p-4 bg-muted rounded-lg text-sm text-muted-foreground">
            {t("rating.loginRequired")}
          </div>
        )}

        {user && (
          <div className="space-y-6">
            {/* Rating inputs */}
            <div className="space-y-4">
              <StarRating
                value={ratingOverall}
                onChange={setRatingOverall}
                label={t("rating.overall")}
              />
              <StarRating
                value={ratingAtmosphere}
                onChange={setRatingAtmosphere}
                label={t("rating.atmosphere")}
              />
              <StarRating
                value={ratingPriceStars}
                onChange={setRatingPriceStars}
                label={t("rating.price")}
              />
              <PriceLevelRating
                value={priceLevel}
                onChange={setPriceLevel}
                label={t("shops.filters.priceLevel") + " " + t("common.optional")}
                currency={getCurrencyForCountry(detectUserCountry())}
              />
              <ParkingLevelRating
                value={parkingRating}
                onChange={setParkingRating}
                label={t("rating.parking") + " " + t("common.optional")}
              />
            </div>

            {/* Meta Fields (Charity & Payment) */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{t("shops.details")} {t("common.optional")}</Label>
                <div className="flex flex-col gap-2">
                  <button
                    type="button"
                    onClick={() => setIsCharity(!isCharity)}
                    className={cn(
                      "flex items-center gap-3 p-3 rounded-md border transition-colors text-left",
                      isCharity ? "bg-primary/10 border-primary text-foreground" : "bg-card hover:bg-muted border-input text-muted-foreground"
                    )}
                  >
                    <Heart className={cn("h-5 w-5", isCharity ? "fill-primary text-primary" : "")} />
                    <span className="text-sm font-medium flex-1">{t("shops.isCharity")}</span>
                    {isCharity && <Check className="h-4 w-4 text-primary" />}
                  </button>
                </div>
              </div>

              <div className="space-y-2">
                <Label>{t("shops.paymentMethods")} {t("common.optional")}</Label>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => setHasCash(!hasCash)}
                    className={cn(
                      "flex-1 flex items-center justify-center gap-2 p-3 rounded-md border transition-colors",
                      hasCash ? "bg-primary/10 border-primary text-foreground" : "bg-card hover:bg-muted border-input text-muted-foreground"
                    )}
                  >
                    <Banknote className="h-5 w-5" />
                    <span className="text-sm font-medium">{t("shops.cash")}</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => setHasCard(!hasCard)}
                    className={cn(
                      "flex-1 flex items-center justify-center gap-2 p-3 rounded-md border transition-colors",
                      hasCard ? "bg-primary/10 border-primary text-foreground" : "bg-card hover:bg-muted border-input text-muted-foreground"
                    )}
                  >
                    <CreditCard className="h-5 w-5" />
                    <span className="text-sm font-medium">{t("shops.card")}</span>
                  </button>
                </div>
              </div>
            </div>

            {/* Categories */}
            <div className="space-y-2">
              <Label>{t("shops.categoriesAvailable")} {t("common.optional")}</Label>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                {Object.keys(categoryIconMap).map((cat) => {
                  const isSelected = selectedCategories.includes(cat);
                  return (
                    <button
                      key={cat}
                      type="button"
                      onClick={() => {
                        if (isSelected) {
                          setSelectedCategories(selectedCategories.filter(c => c !== cat));
                        } else {
                          setSelectedCategories([...selectedCategories, cat]);
                        }
                      }}
                      className={cn(
                        "flex flex-col items-center justify-center gap-2 p-3 rounded-md border h-24 transition-all hover:scale-[1.02]",
                        isSelected
                          ? "bg-primary/10 border-primary text-primary shadow-sm"
                          : "bg-card hover:bg-muted border-input text-muted-foreground"
                      )}
                    >
                      <CategoryIcon category={cat} size={24} />
                      <span className="text-xs text-center font-medium line-clamp-2 leading-tight">
                        {t(`categories.${cat}`)}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Average rating display */}
            {averageRating && (
              <div className="p-3 bg-primary/5 rounded-lg">
                <div className="text-sm font-medium text-muted-foreground">
                  {t("rating.average")}
                </div>
                <div className="text-2xl font-bold text-primary">
                  {averageRating} / 5.0
                </div>
              </div>
            )}

            {/* Comment */}
            <div className="space-y-2">
              <Label htmlFor="comment">{t("rating.commentOptional")}</Label>
              <Textarea
                id="comment"
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                placeholder={t("rating.commentPlaceholder")}
                rows={4}
              />
            </div>

            {/* Photo upload */}
            <div className="space-y-2">
              <Label>{t("rating.photosOptional")}</Label>
              <div className="border border-border rounded-lg p-6 text-center bg-muted/5 flex flex-col items-center gap-4">
                <ImageCaptureInput
                  multiple
                  onImagesSelected={(files) => {
                    setImages(prev => [...prev, ...files].slice(0, 4));
                  }}
                  label={t("rating.addPhotos")}
                />
              </div>



              {images.length > 0 && (
                <div className="grid grid-cols-2 gap-4 mt-2">
                  {images.map((img, index) => (
                    <div key={index} className="relative aspect-square">
                      <img
                        src={(img.startsWith('http') || img.startsWith('/') || img.startsWith('data:')) ? img : `data:image/jpeg;base64,${img}`}
                        alt={`Preview ${index + 1}`}
                        className="w-full h-full object-contain rounded-lg border bg-black/5"
                      />
                      <button
                        type="button"
                        onClick={() => setImages(images.filter((_, i) => i !== index))}
                        className="absolute top-2 right-2 p-1 bg-black/60 hover:bg-black/80 text-white rounded-full transition-colors"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Error message */}
            {error && (
              <div className="p-3 bg-destructive/10 text-destructive rounded-lg text-sm">
                {error}
              </div>
            )}

            {/* Actions */}
            <div className="flex items-center justify-end gap-2 pt-4 border-t">
              {existingReview && (
                <Button
                  variant="ghost"
                  className="text-destructive hover:text-destructive hover:bg-destructive/10 mr-auto"
                  onClick={handleDelete}
                  disabled={isSubmitting}
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  {t("common.delete")}
                </Button>
              )}
              <Button
                variant="outline"
                onClick={() => onOpenChange(false)}
                disabled={isSubmitting}
              >
                {t("shops.cancel")}
              </Button>
              <Button
                onClick={handleSubmit}
                disabled={
                  isSubmitting ||
                  ratingOverall === 0 ||
                  ratingAtmosphere === 0 ||
                  ratingPriceStars === 0
                }
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    {t("routeBuilder.saving")}
                  </>
                ) : (
                  t("rating.save")
                )}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
